import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const BASE = "https://ltwph7gpvb.execute-api.us-east-1.amazonaws.com";

const LAND = [
  [-168,66,-130,70,-95,72,-65,60,-55,47,-75,38,-80,25,-97,22,-87,15,-78,8,-92,14,-105,22,-117,32,-125,42,-125,50,-150,60],
  [-78,8,-60,10,-50,0,-35,-6,-40,-22,-58,-38,-68,-54,-75,-50,-72,-30,-80,-5],
  [-10,36,-9,43,0,49,5,60,20,70,60,70,100,78,140,72,180,68,165,60,140,50,130,35,122,30,120,22,108,18,104,1,98,8,92,22,80,12,73,18,67,25,57,25,50,30,48,22,43,13,35,28,35,36,27,37,12,38,-5,36],
  [-17,21,-5,36,10,37,32,31,43,12,51,12,40,-15,35,-25,20,-35,12,-18,9,4,-8,4,-17,14],
  [113,-22,130,-12,142,-11,153,-27,147,-39,135,-35,115,-34]
];

const pts = (a) => a.reduce((s, v, i) => (i % 2 ? s + (90 - v) + " " : s + (v + 180) + ","), "");

function VesselMap({ v }) {
  const px = v.lon + 180;
  const py = 90 - v.lat;
  const track = (v.track ?? []).map(([la, lo]) => `${lo + 180},${90 - la}`).join(" ");
  return (
    <svg className="map" viewBox={`${px - 45} ${py - 25} 90 50`}>
      {LAND.map((p, i) => <polygon key={i} points={pts(p)} className="land" />)}
      <polyline points={track} className="track" />
      <circle cx={px} cy={py} r="1.5" className="ship" />
      <text x={px + 2.5} y={py - 2} fontSize="3" className="shiptext">{v.name}</text>
    </svg>
  );
}

const Kv = ({ label, children }) => (
  <div className="kv"><span>{label}</span><b>{children}</b></div>
);

const List = ({ items, empty }) =>
  items && items.length
    ? <ul className="list">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    : <p className="note">{empty}</p>;

export default function VesselDetail({ id, onBack }) {
  const [v, setV] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        const res = await fetch(`${BASE}/vessels/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
        setV(data);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [id]);

  return (
    <div>
      <button className="back" onClick={onBack}>← Back to vessels</button>
      {error && <div className="err">{error}</div>}
      {!v && !error && <p className="note">Loading vessel report...</p>}
      {v && (
        <>
          <div className="card">
            <h2>{v.name} <span className="note">IMO {v.vesselId}</span></h2>
            <div className="kvgrid">
              <Kv label="Status">{v.status}</Kv>
              <Kv label="Current position">{v.location}</Kv>
              <Kv label="Destination">{v.destination}</Kv>
              <Kv label="ETA">{v.eta}</Kv>
              <Kv label="Type">{v.type}</Kv>
              <Kv label="Flag">{v.flag}</Kv>
              <Kv label="Age">{new Date().getFullYear() - v.built} years (built {v.built})</Kv>
              <Kv label="Last delivery">{v.lastDelivery}</Kv>
              <Kv label="General acceptance">{v.acceptance}</Kv>
            </div>
          </div>

          <div className="two">
            <div className="card">
              <h2>Position and last track</h2>
              <VesselMap v={v} />
              <p className="note">Schematic map (demo data). A live AIS feed will replace this.</p>
            </div>
            <div className="card">
              <h2>Risk and compliance score</h2>
              <div className="score">{v.riskScore}/100 <span className={`badge ${v.riskLevel.toLowerCase()}`}>{v.riskLevel}</span></div>
              <div className="meter"><i className={v.riskLevel.toLowerCase()} style={{ width: `${v.riskScore}%` }} /></div>
              <table>
                <tbody>
                  {Object.entries(v.risk ?? {}).map(([name, r]) => (
                    <tr key={name}><td>{name}</td><td>+{r.points}</td><td className="note">{r.note}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="two">
            <div className="card">
              <h2>Port-call timeline</h2>
              <div className="tl">
                {(v.portCalls ?? []).map((p, i) => (
                  <div key={i}><b>{p.port}</b> <span className="note">{p.date} · {p.event}</span></div>
                ))}
              </div>
            </div>
            <div className="card">
              <h2>Red flags</h2>
              <List items={v.redFlags} empty="None recorded" />
              <h2 style={{ marginTop: 16 }}>Past rejections</h2>
              <List items={v.rejections} empty="None recorded" />
            </div>
          </div>

          <div className="card">
            <h2>Certificates and compliance</h2>
            <div className="tablewrap">
              <table>
                <thead><tr><th>Certificate</th><th>Status</th><th>Expiry</th></tr></thead>
                <tbody>
                  {(v.certificates ?? []).map((c, i) => (
                    <tr key={i}><td>{c.name}</td><td>{c.status}</td><td>{c.expiry}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}